import subMonths from "date-fns/subMonths";

import { AnalyticsClient } from "@formant/core/client/AnalyticsClient";
import { compress } from "@formant/core/common/compression/compress";
import { ConnectionError } from "@formant/core/common/ConnectionError";
import { narrowError } from "@formant/core/common/narrowError";
import { OutsideDataRetentionPeriodError } from "@formant/core/common/OutsideDataRetentionPeriodError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { toJsonLines } from "@formant/core/common/toJsonLines";
import { TooMuchDataError } from "@formant/core/common/TooMuchDataError";
import { duration } from "@formant/core/model/duration";
import { IQueryByDeviceRequest } from "@formant/core/model/IQueryByDeviceRequest";
import { ISqlColumn } from "@formant/core/model/ISqlColumn";
import { ISqlQuery } from "@formant/core/model/ISqlQuery";
import { ISqlResult } from "@formant/core/model/ISqlResult";
import { ISqlRow } from "@formant/core/model/ISqlRow";
import { ISqlTable } from "@formant/core/model/ISqlTable";
import { IStreamColumn } from "@formant/core/model/IStreamColumn";
import { ITaskReportColumn } from "@formant/core/model/ITaskReportColumn";

import { track } from "../analytics";
import { saveData } from "../component/Observe/utils/saveData";
import { IAppStore } from "./IAppStore";
import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class AnalyticsStore {
    private sqlTablesCache = new StoreCache<undefined, ISqlTable[]>({
        capacity: 10000,
        staleIntervalMs: 60 * 60 * duration.second,
    });
    private streamFilteredCache = new StoreCache<
        IQueryByDeviceRequest,
        IStreamColumn[]
    >({
        capacity: 10000,
        staleIntervalMs: 60 * duration.second,
    });
    private taskReportCache = new StoreCache<undefined, ITaskReportColumn[]>({
        capacity: 10000,
        staleIntervalMs: 60 * duration.second,
    });
    private cache = new StoreCache<ISqlQuery, ISqlResult | undefined>({
        capacity: 10000,
        staleIntervalMs: 5 * 60 * duration.second,
    });

    constructor(
        private analyticsClient: AnalyticsClient,
        private appStore: IAppStore,
        private authStore: IAuthStore,
    ) {}

    public async listSqlTables(): Promise<ISqlTable[] | undefined | false> {
        return this.sqlTablesCache.getAsync(
            undefined,
            async () => await this.fetchListSqlTables(),
        );
    }

    public async realListMatching(
        query?: ISqlQuery,
    ): Promise<ISqlResult | undefined | false> {
        if (!query) {
            return undefined;
        }

        return this.cache.getAsync(
            query,
            async () => await this.fetchListMatching(query),
        );
    }

    public listMatching(query?: ISqlQuery): ISqlResult | undefined {
        if (!query) {
            return undefined;
        }

        return this.cache.get(
            query,
            async () => await this.fetchListMatching(query),
        );
    }

    public listFilteredStreams(
        request: IQueryByDeviceRequest,
    ): IStreamColumn[] | undefined {
        return this.streamFilteredCache.get(
            request,
            async () => await this.fetchFilteredStreams(request),
        );
    }

    public async realListFilteredStreams(
        request: IQueryByDeviceRequest,
    ): Promise<IStreamColumn[] | undefined | false> {
        return this.streamFilteredCache.getAsync(
            request,
            async () => await this.fetchFilteredStreams(request),
        );
    }

    // deprecated
    public listTaskReports(
        request: IQueryByDeviceRequest,
    ): ITaskReportColumn[] | undefined {
        return this.taskReportCache.get(
            undefined,
            async () => await this.fetchTaskReports(request),
        );
    }

    public realGetListTaskReports(
        request: IQueryByDeviceRequest,
    ): Promise<ITaskReportColumn[] | undefined | false> {
        return this.taskReportCache.getAsync(
            undefined,
            async () => await this.fetchTaskReports(request),
        );
    }

    public async fetchListSqlTables(): Promise<ISqlTable[]> {
        const value =
            (await this.analyticsClient.fetchSqlTables(
                await this.authStore.token(),
            )) || [];
        this.sqlTablesCache.set(undefined, value);
        return value;
    }

    public async fetchListMatching(
        query?: ISqlQuery,
    ): Promise<ISqlResult | undefined> {
        if (!query) {
            return undefined;
        }

        const clampToStartOfDay = (dateString: string): string => {
            const date = new Date(dateString);
            date.setUTCHours(0, 0, 0, 0);
            return date.toISOString();
        };

        const clampToEndOfDay = (dateString: string): string => {
            const date = new Date(dateString);
            date.setUTCHours(23, 59, 59, 999);
            return date.toISOString();
        };

        const queryWithDates = {
            ...query,
            start: clampToStartOfDay(
                query?.start || subMonths(new Date(), 1).toISOString(),
            ),
            end: clampToEndOfDay(query?.end || new Date().toISOString()),
            sqlQuery: query.type === "advanced" ? query.sqlQuery : undefined,
        };

        // empty query
        if (
            query.type === "advanced" &&
            query.tableColumns?.length === 0 &&
            query.sqlQuery === undefined
        ) {
            return undefined;
        }

        let value: ISqlResult | undefined;
        try {
            value = await this.analyticsClient.query(
                await this.authStore.token(),
                queryWithDates,
            );
        } catch (error) {
            if (error instanceof TooMuchDataError) {
                return { ...emptySqlResult, error: "Too much data requested" };
            }
            if (
                error instanceof ConnectionError &&
                error.cause instanceof ResponseError &&
                error.cause.statusCode === 408
            ) {
                return { ...emptySqlResult, error: "Query timed out" };
            }
            if (!(error instanceof ResponseError)) {
                if (error instanceof Error) {
                    return { ...emptySqlResult, error: error.message };
                }
                return {
                    ...emptySqlResult,
                    error: parseErrorMessageFromResponse(JSON.stringify(error)),
                };
            }
            const responseError = narrowError(error, ResponseError);
            const { statusCode } = responseError;
            if (statusCode === 422) {
                return {
                    ...emptySqlResult,
                    error: parseErrorMessageFromResponse(responseError.message),
                };
            } else if (statusCode === 413) {
                return { ...emptySqlResult, error: "Too much data requested" };
            } else if (statusCode === 408) {
                return { ...emptySqlResult, error: "Query timed out" };
            }
            return emptySqlResult;
        }

        if (value?.rows && value?.rowCount > 0) {
            this.cache.set(query, value);
        }

        return value;
    }

    public async fetchFilteredStreams(
        request: IQueryByDeviceRequest,
    ): Promise<IStreamColumn[]> {
        const value = await this.analyticsClient.listFilteredStreams(
            await this.authStore.token(),
            request.deviceIds,
        );
        this.streamFilteredCache.set(request, value);
        return value;
    }

    // @deprecated
    public async fetchTaskReports(
        _: IQueryByDeviceRequest,
    ): Promise<ITaskReportColumn[]> {
        return [] as ITaskReportColumn[];
    }

    public async refreshList(query: ISqlQuery) {
        return this.cache.refresh(
            query,
            async () => await this.fetchListMatching(query),
        );
    }

    public async export(query: ISqlQuery): Promise<void> {
        try {
            const resp = await this.realListMatching(query);
            if (!resp) {
                return;
            }
            const exportJson = formatExportJSON(resp);
            const lines = toJsonLines(exportJson);
            const compressed = await compress(lines);
            track("Analytics JSON (Direct Download)");
            saveData(
                `FormantExport-${Date.now()}.jsonl.gz`,
                compressed as BlobPart,
                "application/gzip",
            );
        } catch (error) {
            if (error instanceof TooMuchDataError) {
                this.appStore.setSnackbar(
                    "Too much data in selected time range",
                );
            } else if (error instanceof OutsideDataRetentionPeriodError) {
                this.appStore.setSnackbar(
                    "Selected time range is outside data retention period",
                );
            } else {
                throw error;
            }
        }
    }

    public async exportSheet(query: ISqlQuery): Promise<void> {
        try {
            const resp = await this.realListMatching(query);
            if (!resp) {
                return;
            }

            const exportCSV = formatExportCSV(resp as ISqlResult);
            track("Analytics CSV (Direct Download)");
            saveData(
                `FormantExport-${Date.now()}.csv`,
                exportCSV,
                "application/csv",
            );
        } catch (error) {
            if (error instanceof TooMuchDataError) {
                this.appStore.setSnackbar(
                    "Too much data in selected time range",
                );
            } else if (error instanceof OutsideDataRetentionPeriodError) {
                this.appStore.setSnackbar(
                    "Selected time range is outside data retention period",
                );
            } else {
                throw error;
            }
        }
    }
}

function formatExportJSON(data: ISqlResult): any[] {
    const { columns, rows } = data;
    const keys = columns.map((column: ISqlColumn) => column.name);
    const json = rows.map((row: ISqlRow) => {
        const newRow: any = {};
        keys.forEach((key: any) => {
            newRow[`${key}`] = row[`${key}`] !== "None" ? row[`${key}`] : "";
        });
        return newRow;
    });
    return json;
}

function formatExportCSV(data: ISqlResult): string {
    const csv = [];
    const rowData = data.rows;
    const keys = [
        ...new Set(data.columns.map((column: ISqlColumn) => column.name)),
    ];
    csv.push([...keys].join(","));

    const lines = rowData.map((row: any) => {
        const newRow: any = [];
        keys.forEach((key: any) => {
            newRow.push(
                row[`${key}`] !== "None" && key in row
                    ? String(row[`${key}`])
                    : "",
            );
        });
        return newRow.join(",");
    });
    csv.push(...lines);
    return csv.join("\n");
}

function parseErrorMessageFromResponse(response: string): string | undefined {
    const match = response.match(/{"message":"(.+)"}/);
    try {
        if (!match || !match[1]) {
            return JSON.parse(response.replace("Response error", ""))
                ?.responseBody?.message;
        }
        return match[1].replace(/\n/g, " ").replace(`\\n`, " ");
    } catch {
        return response;
    }
}

const emptySqlResult = {
    rows: [],
    columns: [],
    rowCount: 0,
    sqlText: "",
};
